import { base64Encode } from "@opencode-ai/core/util/encode"
import { createQuery } from "@tanstack/solid-query"
import { useNavigate, useSearchParams } from "@solidjs/router"
import { type Accessor, createEffect, createMemo, createResource } from "solid-js"
import type { PromptInputControls } from "@/components/prompt-input/contracts"
import type { PromptProjectControls } from "@/components/prompt-project-selector"
import { useDirectoryPicker } from "@/components/directory-picker"
import { useGlobal } from "@/context/global"
import { useLayout } from "@/context/layout"
import { useLocal, type ModelSelection } from "@/context/local"
import type { QueryOptionsApi } from "@/context/server-sync"
import { useServerSDK } from "@/context/server-sdk"
import { serverName, ServerConnection, useServer } from "@/context/server"
import { useSDK } from "@/context/sdk"
import { useSync } from "@/context/sync"
import { useTabs } from "@/context/tabs"
import { useProviders } from "@/hooks/use-providers"
import { pathKey } from "@/utils/path-key"
import { coworkAgentMode, getSessionContextByRemote } from "@/cowork/api"
import { coworkAgentRegistry } from "@/cowork/report-template-registry.generated"
import { canonicalRuntimeAgent } from "@/cowork/agent-runtime"

const coworkAgentSelectionPrefix = "cowork.agent.sessionRuntimeAgent."
const fallbackCoworkAgentSelections = new Map<string, string>()
type CoworkAgentSelectionStorage = {
  get(sessionID: string): string | undefined
  set(sessionID: string, runtimeName: string): unknown
}

const persistentCoworkAgentSelections = {
  get(sessionID: string) {
    if (typeof localStorage !== "object") return fallbackCoworkAgentSelections.get(sessionID)
    try {
      return localStorage.getItem(coworkAgentSelectionPrefix + sessionID) ?? undefined
    } catch {
      return fallbackCoworkAgentSelections.get(sessionID)
    }
  },
  set(sessionID: string, runtimeName: string) {
    fallbackCoworkAgentSelections.set(sessionID, runtimeName)
    if (typeof localStorage !== "object") return
    try {
      localStorage.setItem(coworkAgentSelectionPrefix + sessionID, runtimeName)
    } catch {
      // Keep the in-memory selection when browser storage is unavailable.
    }
  },
}

export function createCoworkAgentSelection(
  select: (name: string) => unknown,
  selections: CoworkAgentSelectionStorage = persistentCoworkAgentSelections,
) {
  let initialized = ""

  const apply = (sessionID: string, runtimeName: string) => {
    runtimeName = canonicalRuntimeAgent(runtimeName)
    const key = `${sessionID}:${runtimeName}`
    if (key === initialized) return
    if (select(runtimeName) === false) return
    initialized = key
  }

  return {
    initialize(sessionID: string, templateID: unknown, contextSessionID: string) {
      // Resources retain the previous response while the next tab's context loads.
      if (contextSessionID !== sessionID) return
      const assigned = typeof templateID === "string" && templateID in coworkAgentRegistry
        ? coworkAgentRegistry[templateID as keyof typeof coworkAgentRegistry].runtime_agent
        : "custom-report-agent"
      apply(sessionID, selections.get(sessionID) ?? assigned)
    },
    restore(sessionID: string) {
      const selected = selections.get(sessionID)
      if (selected) apply(sessionID, selected)
    },
    select(sessionID: string, runtimeName: string) {
      selections.set(sessionID, runtimeName)
      apply(sessionID, runtimeName)
    },
  }
}

export function createPromptInputController(input: {
  sessionKey: Accessor<string>
  sessionID: Accessor<string | undefined>
  queryOptions: Pick<QueryOptionsApi, "agents" | "providers">
  model?: ModelSelection
}) {
  const layout = useLayout()
  const local = useLocal()
  const sdk = useSDK()
  const sync = useSync()
  const providers = useProviders(() => sdk().directory)
  const view = layout.view(input.sessionKey)
  const agentsQuery = createQuery(() => input.queryOptions.agents(pathKey(sdk().directory)))
  const globalProvidersQuery = createQuery(() => input.queryOptions.providers(null))
  const providersQuery = createQuery(() => input.queryOptions.providers(pathKey(sdk().directory)))
  const [coworkContext] = createResource(
    () => (coworkAgentMode() ? input.sessionID() : undefined),
    (sessionID) => getSessionContextByRemote(sessionID),
  )
  const coworkAgent = createCoworkAgentSelection((name) => {
    if (!local.agent.list().some((agent) => agent.name === name)) return false
    local.agent.set(name)
  })

  createEffect(() => {
    const sessionID = input.sessionID()
    local.agent.list()
    if (coworkAgentMode() && sessionID) coworkAgent.restore(sessionID)
  })

  createEffect(() => {
    const context = coworkContext()
    const agent = context?.session_metadata?.agent
    if (!coworkAgentMode() || !context || !agent || typeof agent !== "object") return
    const sessionID = input.sessionID()
    if (!sessionID) return
    local.agent.list()
    coworkAgent.initialize(
      sessionID,
      (agent as { template_id?: unknown }).template_id,
      context.remote_opencode_session_id,
    )
  })
  return createMemo<PromptInputControls>(() => {
    return {
      agents: {
        available: sync().data.agent,
        options: local.agent.list().map((agent) => agent.name),
        current: local.agent.current()?.name ?? "",
        loading: agentsQuery.isLoading,
        visible: local.agent.visible(),
        select: (name) => {
          if (!name) return
          const sessionID = input.sessionID()
          if (coworkAgentMode() && sessionID) return coworkAgent.select(sessionID, name)
          local.agent.set(name)
        },
      },
      model: {
        selection: input.model ?? local.model,
        paid: providers.paid().length > 0,
        loading:
          (local.agent.visible() && agentsQuery.isLoading) ||
          providersQuery.isLoading ||
          globalProvidersQuery.isLoading,
      },
      session: {
        id: input.sessionID(),
        tabs: layout.tabs(input.sessionKey),
        reviewPanel: view.reviewPanel,
      },
    }
  })
}

export function createPromptProjectControls() {
  const navigate = useNavigate()
  const layout = useLayout()
  const server = useServer()
  const serverSDK = useServerSDK()
  const sdk = useSDK()
  const tabs = useTabs()
  const global = useGlobal()
  const pickDirectory = useDirectoryPicker()
  const [search] = useSearchParams<{ draftId?: string }>()
  const projectServer = () => serverSDK().server
  const projectServerCtx = createMemo(() => global.ensureServerCtx(projectServer()))
  const projects = createMemo(() => {
    if (server.list.length <= 1) {
      return search.draftId ? projectServerCtx().projects.list() : layout.projects.list()
    }
    return server.list.flatMap((conn) => {
      const item = { key: ServerConnection.key(conn), name: serverName(conn) }
      return global
        .ensureServerCtx(conn)
        .projects.list()
        .map((project) => ({ ...project, server: item }))
    })
  })
  const selectProject = (worktree: string, serverKey?: string) => {
    const conn = serverKey ? server.list.find((conn) => ServerConnection.key(conn) === serverKey) : projectServer()
    if (search.draftId) {
      if (!conn) return
      const target = global.ensureServerCtx(conn)
      target.projects.open(worktree)
      target.projects.touch(worktree)
      tabs.updateDraft(search.draftId, { server: ServerConnection.key(conn), directory: worktree })
      return
    }

    if (!serverKey) {
      layout.projects.open(worktree)
      server.projects.touch(worktree)
      navigate(`/${base64Encode(worktree)}/session`)
      return
    }

    if (!conn) return
    const target = global.ensureServerCtx(conn)
    target.projects.open(worktree)
    target.projects.touch(worktree)
    server.setActive(ServerConnection.key(conn))
    navigate(`/${base64Encode(worktree)}/session`)
  }

  const addProject = (title: string, serverKey?: string) => {
    const conn = serverKey ? server.list.find((conn) => ServerConnection.key(conn) === serverKey) : projectServer()
    if (!conn) return
    pickDirectory({
      server: conn,
      title,
      onSelect: (result) => {
        const directory = Array.isArray(result) ? result[0] : result
        if (directory) selectProject(directory, serverKey)
      },
    })
  }

  return createMemo<PromptProjectControls>(() => ({
    available: projects(),
    directory: sdk().directory,
    server: server.list.length > 1 ? ServerConnection.key(projectServer()) : undefined,
    select: selectProject,
    add: addProject,
  }))
}
